/** Parent children detail tab. */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ArrowUpRight, GraduationCap } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailAvailabilityGrid } from '@/components/common/details/DetailAvailabilityGrid';
import { DetailChipList } from '@/components/common/details/DetailChipList';
import { Button } from '@/components/forms/Button';
import { Spinner } from '@/components/ui/spinner';
import {
  REMOTE_SUBJECT_CONFIG,
  SCHOOL_SITUATION_CONFIG,
  STUDENT_ACTIVE_STATUS_CONFIG,
  STUDENT_PENDING_STATUS_CONFIG,
} from '@/components/parents/parent-configs';
import { STUDENT_SITUATION_CONFIG } from '@/constants/shared-catalog';
import type { ParentRecord } from '@/types/parent.types';
import type { StudentRecord } from '@/types/remote/student.types';
import {
  formatDate,
  getProgramLabel,
  getStudentCategory,
  getStudentSituation,
  normalizeSlots,
} from '@/components/parents/parent-table-utils';

/** Program id and display labels. */
type Program = { id: string; name?: string; label?: string; abbreviation?: string };

/** Compact child card used inside {@link ParentChildrenTab}. */
interface ChildCardProps {
  child: StudentRecord;
  programs: Program[];
  onNavigate: () => void;
}

/** Compact child card inside the children tab. */
const ChildCard: React.FC<ChildCardProps> = ({ child, programs, onNavigate }) => {
  const registrationInformation = (child.sections?.['registration_information'] as any) ?? {};
  const md = (child.sections?.['matching_data'] as any) ?? {};

  const { category, status } = getStudentCategory({
    onboardingStatus: child.onboardingStatus,
    folderSituation: child.folderSituation,
    user: child.user,
    linkedUserId: child.linkedUserId,
  });

  const statusConfig =
    category === 'pre_registered'
      ? STUDENT_PENDING_STATUS_CONFIG[status as keyof typeof STUDENT_PENDING_STATUS_CONFIG]
      : STUDENT_ACTIVE_STATUS_CONFIG[status as keyof typeof STUDENT_ACTIVE_STATUS_CONFIG];

  const studentSituation = getStudentSituation({
    onboardingStatus: child.onboardingStatus,
    studentSituation: child.studentSituation,
  });
  const situationConfig = STUDENT_SITUATION_CONFIG[studentSituation];
  const StatusIcon = statusConfig?.icon ?? CheckCircle2;

  return (
    <div className="border rounded-lg p-4 bg-card space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold">
            {child.user?.firstName ?? registrationInformation.firstName ?? '—'}{' '}
            {child.user?.lastName ?? registrationInformation.lastName ?? '—'}
          </span>
          {statusConfig && (
            <LabelBadge
              label={statusConfig.label}
              icon={StatusIcon}
              variant={statusConfig.badgeVariant}
              color={statusConfig.badgeColor}
              size="sm"
            />
          )}
          {situationConfig && (
            <LabelBadge
              label={situationConfig.label}
              icon={situationConfig.icon}
              variant={situationConfig.badgeVariant}
              color={situationConfig.badgeColor}
              size="sm"
            />
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          icon={ArrowUpRight}
          onClick={onNavigate}
          tooltipText="Voir les détails de l'élève"
          aria-label="Voir les détails de l'élève"
        />
      </div>

      <div className="space-y-3">
        <DetailField
          label="Date de naissance"
          value={formatDate(registrationInformation.dateOfBirth ?? child.user?.birthDate)}
        />
        <DetailField
          label="Classe"
          value={registrationInformation.level?.levelName ?? registrationInformation.level}
        />
        {registrationInformation.schoolSituation && (
          <DetailField
            label="Situation scolaire"
            value={
              <LabelBadge
                label={
                  SCHOOL_SITUATION_CONFIG[registrationInformation.schoolSituation]?.label ??
                  registrationInformation.schoolSituation
                }
                icon={
                  SCHOOL_SITUATION_CONFIG[registrationInformation.schoolSituation]?.icon ??
                  GraduationCap
                }
                variant={
                  SCHOOL_SITUATION_CONFIG[registrationInformation.schoolSituation]?.badgeVariant ??
                  'outlined'
                }
                color={SCHOOL_SITUATION_CONFIG[registrationInformation.schoolSituation]?.badgeColor}
                size="sm"
              />
            }
          />
        )}
        {md.subject && (
          <DetailField
            label="Matière"
            value={
              <LabelBadge
                label={REMOTE_SUBJECT_CONFIG[md.subject]?.label ?? md.subject}
                icon={REMOTE_SUBJECT_CONFIG[md.subject]?.icon}
                variant={REMOTE_SUBJECT_CONFIG[md.subject]?.badgeVariant ?? 'outlined'}
                color={REMOTE_SUBJECT_CONFIG[md.subject]?.badgeColor}
              />
            }
          />
        )}
        {md.programs && Array.isArray(md.programs) && md.programs.length > 0 && (
          <DetailField
            label="Programme(s)"
            value={
              <DetailChipList
                items={md.programs.map((p: any, i: number) => {
                  const programId = p?.programId ?? p?.id;
                  const matched = programId ? programs.find((pr) => pr.id === programId) : null;
                  const label =
                    matched?.name ?? matched?.label ?? matched?.abbreviation ?? getProgramLabel(p);
                  return { id: String(programId ?? i), label };
                })}
                colorScheme="color-3"
                variant="filled"
              />
            }
          />
        )}
        {md.availability && Array.isArray(md.availability) && md.availability.length > 0 && (
          <DetailField
            label="Disponibilités"
            value={
              <DetailAvailabilityGrid slots={normalizeSlots(md.availability)} variant="compact" />
            }
          />
        )}
      </div>
    </div>
  );
};

/** Props for {@link ParentChildrenTab}. */
interface ParentChildrenTabProps {
  record: ParentRecord;
  childList: StudentRecord[];
  isLoadingChildren: boolean;
  programs: Program[];
}

/** Parent children detail tab. */
export const ParentChildrenTab: React.FC<ParentChildrenTabProps> = ({
  record,
  childList,
  isLoadingChildren,
  programs,
}) => {
  const navigate = useNavigate();
  const parentName = `${record.user?.firstName ?? ''} ${record.user?.lastName ?? ''}`.trim();

  return (
    <div className="space-y-4 mt-6">
      {isLoadingChildren && (
        <div className="flex items-center justify-center py-8">
          <Spinner size="md" />
        </div>
      )}
      {!isLoadingChildren && childList.length === 0 && (
        <p className="text-sm text-muted-foreground">Aucun enfant lié.</p>
      )}
      {!isLoadingChildren &&
        childList.map((child) => (
          <ChildCard
            key={child.id}
            child={child}
            programs={programs}
            onNavigate={() =>
              navigate(`/students?view=${child.id}`, {
                state: { linkedUserId: record.userId, personName: parentName },
              })
            }
          />
        ))}
    </div>
  );
};
